import React from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, ScrollReveal, StatsBar, CTASection, TestimonialCard } from '../../components/UI';

const LocalSEO = () => {
  const features = [
    {
      icon: '📍',
      title: 'Google Business Profile Optimization',
      desc: 'Your GBP is often the first impression customers have of your business. We fully optimize every element—categories, description, photos, posts, Q&A, and more—to maximize your visibility in the local pack.'
    },
    {
      icon: '🏗️',
      title: 'Citation Building & Cleanup',
      desc: 'We build consistent citations across 50+ high-authority directories and clean up any inconsistent or duplicate listings that confuse Google and hurt your rankings.'
    },
    {
      icon: '⭐',
      title: 'Review Generation & Management',
      desc: 'Reviews are crucial for local rankings and customer trust. We implement proven strategies to generate more positive reviews and help you respond professionally to all feedback.'
    },
    {
      icon: '🔗',
      title: 'Local Link Building',
      desc: 'We build high-quality backlinks from local businesses, organizations, and media outlets that signal to Google that you\'re a trusted authority in your community.'
    },
    {
      icon: '📝',
      title: 'Locally-Optimized Content',
      desc: 'We create content that targets local search intent—service area pages, location-specific blog posts, and local landing pages that capture traffic from surrounding areas.'
    },
    {
      icon: '📊',
      title: 'Rank Tracking & Reporting',
      desc: 'We track your rankings across all your target keywords and locations, providing clear monthly reports that show exactly how your visibility is improving.'
    },
  ];

  const rankings = [
    { position: '1-3', label: 'Local Pack', desc: 'The top 3 map results that capture 44% of local clicks' },
    { position: '1-10', label: 'Organic Results', desc: 'First page organic listings for your target keywords' },
    { position: '4.5+', label: 'Star Rating', desc: 'Maintain a strong review profile that builds trust' },
  ];

  return (
    <div>
      <PageHeader 
        title="Local SEO Services"
        subtitle="Dominate local search results. When customers in your area search for your services, make sure they find YOU—not your competitors."
        breadcrumb="Local SEO"
      />

      <StatsBar />

      {/* Why Local SEO */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }}>
            <ScrollReveal>
              <div>
                <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Why Local SEO?</span>
                <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10, marginBottom: 20 }}>
                  46% of Google Searches Have Local Intent
                </h2>
                <p style={{ color: '#555', fontSize: 16, lineHeight: 1.8, marginBottom: 16 }}>
                  When someone searches "plumber near me" or "best roofing company in [city]", they're ready to hire. Local SEO puts your business in front of these high-intent customers at the exact moment they're looking for help.
                </p>
                <p style={{ color: '#555', fontSize: 16, lineHeight: 1.8, marginBottom: 16 }}>
                  The local pack (those top 3 map results) captures 44% of all clicks. If you're not there, you're invisible to nearly half of potential customers searching for your services.
                </p>
                <p style={{ color: '#555', fontSize: 16, lineHeight: 1.8, marginBottom: 24 }}>
                  We've helped dozens of local businesses climb from page 3 to the top of local search results—and the phone calls and leads that follow are proof of what's possible.
                </p>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  {rankings.map((item, i) => (
                    <div key={i} style={{ background: '#f8f9fa', borderRadius: 12, padding: 20, flex: '1 1 150px' }}>
                      <div style={{ fontSize: 28, fontWeight: 800, color: '#ff6b35' }}>{item.position}</div>
                      <div style={{ fontWeight: 700, color: '#1a1a2e', fontSize: 14, marginTop: 4 }}>{item.label}</div>
                      <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>{item.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <div style={{ background: 'linear-gradient(135deg, #1a1a2e, #16213e)', borderRadius: 24, padding: 40 }}>
                <h3 style={{ color: 'white', fontSize: 22, fontWeight: 700, marginBottom: 24 }}>Local SEO Impact</h3>
                {[
                  { stat: '76%', desc: 'of people who search for something nearby visit a business within a day' },
                  { stat: '28%', desc: 'of local searches result in a purchase' },
                  { stat: '97%', desc: 'of consumers search online for local businesses' },
                  { stat: '88%', desc: 'of mobile local searches result in a call or visit within 24 hours' },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: 16, marginBottom: 20, alignItems: 'flex-start' }}>
                    <div style={{ fontSize: 24, fontWeight: 800, color: '#ff6b35', minWidth: 70 }}>{item.stat}</div>
                    <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 15, lineHeight: 1.6 }}>{item.desc}</div>
                  </div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section style={{ padding: '80px 24px', background: '#f8f9fa' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 50 }}>
              <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>What's Included</span>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10, marginBottom: 16 }}>
                Complete Local SEO Solution
              </h2>
              <p style={{ color: '#666', maxWidth: 600, margin: '0 auto', fontSize: 17, lineHeight: 1.7 }}>
                Everything you need to dominate local search in your market.
              </p>
            </div>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 24 }}>
            {features.map((feature, i) => (
              <ScrollReveal key={i} delay={i * 0.08}>
                <div style={{ background: 'white', borderRadius: 20, padding: 32, height: '100%', border: '1px solid #eee' }}>
                  <div style={{ fontSize: 40, marginBottom: 16 }}>{feature.icon}</div>
                  <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e', marginBottom: 12 }}>{feature.title}</h3>
                  <p style={{ color: '#666', fontSize: 15, lineHeight: 1.7, margin: 0 }}>{feature.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* GBP Section */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 50 }}>
              <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Google Business Profile</span>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10, marginBottom: 16 }}>
                Your GBP Is Your Digital Storefront
              </h2>
              <p style={{ color: '#666', maxWidth: 700, margin: '0 auto', fontSize: 17, lineHeight: 1.7 }}>
                Most businesses set up their Google Business Profile and forget about it. That's a mistake. Your GBP is often the first thing customers see—and it directly impacts your local rankings.
              </p>
            </div>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20 }}>
            {[
              { title: 'Category Optimization', desc: 'Primary and secondary categories that match your services' },
              { title: 'Complete Information', desc: 'Hours, services, attributes fully filled out' },
              { title: 'Photo Optimization', desc: 'High-quality photos that showcase your business' },
              { title: 'Regular Posts', desc: 'Weekly updates that keep your profile fresh' },
              { title: 'Q&A Management', desc: 'Proactive answers to common questions' },
              { title: 'Review Responses', desc: 'Professional responses to all reviews' },
            ].map((item, i) => (
              <ScrollReveal key={i} delay={i * 0.05}>
                <div style={{ background: '#f8f9fa', borderRadius: 16, padding: 24, textAlign: 'center' }}>
                  <h4 style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>{item.title}</h4>
                  <p style={{ color: '#666', fontSize: 14, lineHeight: 1.6, margin: 0 }}>{item.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section style={{ padding: '80px 24px', background: '#f8f9fa' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 50 }}>
              <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Investment</span>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10, marginBottom: 16 }}>
                Local SEO Packages
              </h2>
            </div>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {[
              { name: 'Local Essentials', price: '$1,500', desc: 'Perfect for single-location businesses', features: ['GBP Optimization', '25 Citation Builds', 'Review Generation Setup', 'Monthly Reporting', 'Keyword Tracking (25)', 'Quarterly Strategy Call'] },
              { name: 'Local Domination', price: '$2,500', desc: 'For businesses serious about market leadership', features: ['Everything in Essentials', '50+ Citation Builds', 'Local Link Building', 'On-Page SEO', 'Keyword Tracking (50)', 'Monthly Strategy Call'], popular: true },
            ].map((plan, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <div style={{ 
                  background: 'white', 
                  borderRadius: 20, 
                  padding: 36, 
                  border: plan.popular ? '2px solid #ff6b35' : '1px solid #eee',
                  position: 'relative',
                  height: '100%'
                }}>
                  {plan.popular && (
                    <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: '#ff6b35', color: 'white', padding: '4px 16px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                      Most Popular
                    </div>
                  )}
                  <h3 style={{ fontSize: 22, fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>{plan.name}</h3>
                  <div style={{ fontSize: 36, fontWeight: 800, color: '#ff6b35', marginBottom: 4 }}>{plan.price}<span style={{ fontSize: 16, color: '#888' }}>/mo</span></div>
                  <div style={{ color: '#666', fontSize: 14, marginBottom: 24 }}>{plan.desc}</div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, marginBottom: 28 }}>
                    {plan.features.map((feature, j) => (
                      <li key={j} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, color: '#555', fontSize: 15 }}>
                        <span style={{ color: '#22c55e' }}>✓</span> {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact" className={plan.popular ? 'btn-primary' : 'btn-secondary'} style={{ display: 'block', textAlign: 'center', padding: 14, borderRadius: 50, fontSize: 15 }}>
                    Get Started
                  </Link>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <ScrollReveal>
            <TestimonialCard 
              name="Mike Johnson"
              company="Johnson Roofing"
              text="Before Crescit, we were nowhere to be found on Google Maps. Now we're in the top 3 for every major search term in our area. We went from 5 leads a month to over 40. Best investment we've ever made."
            />
          </ScrollReveal>
        </div>
      </section>

      <CTASection 
        title="Ready to Dominate Local Search?"
        subtitle="Let's get your business to the top of Google Maps where customers can find you."
        buttonText="Get Your Free Local SEO Audit →"
      />
    </div>
  );
};

export default LocalSEO;
